import { ReactNode, useState } from 'react'
import { isSameDay } from 'date-fns'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Box, F0Text } from '@factorialco/f0-react'
import i18n from 'modules/core/lib/i18n'
import { useCopiedSourceFeedback } from 'modules/shift_management/hooks/useClipboard'
import type {
  TimeGranularity,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

interface Props {
  rowHeaderLabel?: ReactNode
  rowHeaderWidth: number
  minColumnWidth: number
  gridMinWidth: number
  showGridLines: boolean
  stickyHeaders: boolean
  granularity: TimeGranularity
  slots: TimeSlot[]
  timeSlotFormatter: (slot: TimeSlot) => ReactNode
  selectedColumnIndexes?: Set<number> | null
  isAllSelected?: boolean
  onColumnHeaderSelected?: (
    slotIndex: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  onColumnHeaderDragStart?: (context: {
    axis: 'row' | 'column'
    index: number
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  }) => void
  onCornerCellSelected?: () => void
}

const TimeGridHeader = ({
  rowHeaderLabel,
  rowHeaderWidth,
  minColumnWidth,
  gridMinWidth,
  showGridLines,
  stickyHeaders,
  granularity,
  slots,
  timeSlotFormatter,
  selectedColumnIndexes,
  isAllSelected,
  onColumnHeaderSelected,
  onColumnHeaderDragStart,
  onCornerCellSelected,
}: Props) => {
  const [hoveredHeaderIndex, setHoveredHeaderIndex] = useState<number | 'corner' | null>(null)
  const { copiedColumnIndexes } = useCopiedSourceFeedback()

  const renderSlotLabel = (slot: TimeSlot, formattedSlot: ReactNode) => {
    if (typeof formattedSlot !== 'string' && typeof formattedSlot !== 'number') {
      return formattedSlot
    }

    const label = String(formattedSlot).trim()
    const dayNumber = label.match(/\d+/)?.[0] ?? ''
    const weekday = label.replace(/\d+/g, '').trim()
    const isTodaySlot = isSameDay(slot.start, new Date())

    if (isTodaySlot && dayNumber && weekday) {
      return (
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
          <F0Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            background="accent-bold"
            borderRadius="xs"
            height="5"
            width="6"
          >
            <F0Text variant="inverse" content={dayNumber} align="center" />
          </F0Box>
          <F0Text content={weekday} variant="description" />
        </F0Box>
      )
    }

    if (dayNumber && weekday) {
      return (
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
          <F0Text content={dayNumber} variant="description" />
          {granularity !== 'week' ? (
            <F0Text
              content={weekday}
              variant="description"
              style={{ color: 'hsl(var(--neutral-40))' }}
            />
          ) : (
            <F0Text content={weekday} variant="description" />
          )}
        </F0Box>
      )
    }

    return <F0Text content={label} />
  }

  return (
    <Box
      role="row"
      data-testid="time-grid-header"
      data-grid-width={gridMinWidth}
      background="grey000White"
      style={{
        display: 'grid',
        gridTemplateColumns: `${rowHeaderWidth}px repeat(${slots.length}, minmax(${minColumnWidth}px, 1fr))`,
        minWidth: String(gridMinWidth),
        position: stickyHeaders ? 'sticky' : 'relative',
        top: '0px',
        zIndex: stickyHeaders ? '3' : '1',
        userSelect: 'none',
      }}
    >
      <F0Box
        role="columnheader"
        position="sticky"
        paddingTop="xs"
        paddingRight="xs"
        paddingBottom="sm"
        paddingLeft="lg"
        minHeight="9"
        display="flex"
        alignItems="center"
        gap="sm"
        onClick={onCornerCellSelected}
        onMouseEnter={() => setHoveredHeaderIndex('corner')}
        onMouseLeave={() => setHoveredHeaderIndex(null)}
        aria-label={onCornerCellSelected ? i18n.t('common.select_all') : undefined}
        aria-pressed={onCornerCellSelected ? isAllSelected : undefined}
        borderBottom={
          showGridLines && hoveredHeaderIndex !== 'corner' && !isAllSelected ? 'default' : undefined
        }
        borderColor={showGridLines ? 'secondary' : undefined}
        background="primary"
      >
        {typeof rowHeaderLabel === 'string' || typeof rowHeaderLabel === 'number' ? (
          <F0Text content={String(rowHeaderLabel)} />
        ) : (
          rowHeaderLabel
        )}
        {showGridLines && (hoveredHeaderIndex === 'corner' || isAllSelected) ? (
          <Box
            aria-hidden="true"
            className="bg-f1-icon-positive"
            style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              height: '2px',
              opacity: isAllSelected ? '0.7' : '0.3',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </F0Box>
      {slots.map((slot) => {
        const isColumnSelected = isAllSelected || (selectedColumnIndexes?.has(slot.index) ?? false)
        const isColumnCopied = copiedColumnIndexes.has(slot.index)
        const formattedSlot = timeSlotFormatter(slot)
        const handleColumnHeaderMouseDown = onColumnHeaderDragStart
          ? (e: React.MouseEvent<HTMLDivElement>) => {
              e.preventDefault()
              onColumnHeaderDragStart({
                axis: 'column',
                index: slot.index,
                modifiers: {
                  shiftKey: e.shiftKey,
                  metaKey: e.metaKey,
                  ctrlKey: e.ctrlKey,
                },
              })
            }
          : undefined

        return (
          <F0Box
            key={slot.index}
            role="columnheader"
            position="relative"
            paddingTop="xs"
            paddingRight="sm"
            paddingBottom="sm"
            paddingLeft="sm"
            minHeight="9"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="xs"
            onClick={
              onColumnHeaderSelected
                ? (event) =>
                    onColumnHeaderSelected(slot.index, {
                      shiftKey: event.shiftKey,
                      metaKey: event.metaKey,
                      ctrlKey: event.ctrlKey,
                    })
                : undefined
            }
            onMouseEnter={() => setHoveredHeaderIndex(slot.index)}
            onMouseLeave={() => setHoveredHeaderIndex(null)}
            onMouseDown={handleColumnHeaderMouseDown}
            data-time-grid-column-index={slot.index}
            borderBottom={
              showGridLines
                ? isColumnSelected || isColumnCopied || hoveredHeaderIndex === slot.index
                  ? 'thick'
                  : 'default'
                : undefined
            }
            borderStyle={showGridLines ? 'solid' : undefined}
            borderColor={
              showGridLines
                ? isColumnSelected || isColumnCopied
                  ? 'selected-bold'
                  : hoveredHeaderIndex === slot.index && !isColumnSelected && !isColumnCopied
                    ? 'selected'
                    : 'secondary'
                : undefined
            }
          >
            {renderSlotLabel(slot, formattedSlot)}
          </F0Box>
        )
      })}
    </Box>
  )
}

export default TimeGridHeader
